import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Image,
  Alert,
  ActivityIndicator,
  SafeAreaView,
  Dimensions,
  Modal
} from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import QRCode from 'react-native-qrcode-svg';
import { getMyCoupons } from '../services/couponsApi';
import AppHeader from '../components/AppHeader';

const { width, height } = Dimensions.get('window');

export default function IndobestCouponDetailsScreen() {
  const navigation = useNavigation();
  const route = useRoute();
  const { coupon: initialCoupon } = route.params || {};
  
  const [coupon, setCoupon] = useState(initialCoupon || {});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Custom Alert Modal State
  const [alertModal, setAlertModal] = useState({
    visible: false,
    title: '',
    message: '',
    type: 'error', // 'error' or 'success'
    onConfirm: null
  });

  // Custom Alert Functions
  const showCustomAlert = (title, message, type = 'error', onConfirm = null) => {
    setAlertModal({
      visible: true,
      title,
      message,
      type,
      onConfirm
    });
  };

  const hideCustomAlert = () => {
    setAlertModal({
      visible: false,
      title: '',
      message: '',
      type: 'error',
      onConfirm: null
    });
  };

  const handleAlertConfirm = () => {
    if (alertModal.onConfirm) {
      alertModal.onConfirm();
    }
    hideCustomAlert();
  };

  // Load latest coupon data from my-coupons API
  const loadLatestCouponData = async () => {
    try {
      setRefreshing(true);
      const token = await AsyncStorage.getItem('userToken');
      if (!token) {
        console.log('No token found, skipping coupon data refresh');
        return;
      }

      // console.log('Loading latest coupon data for ID:', coupon.id);
      console.log('Loading latest coupon data');
      const myCouponsResponse = await getMyCoupons();
      // console.log('My coupons response:', myCouponsResponse);

      if (myCouponsResponse && myCouponsResponse.data && myCouponsResponse.data.coupons && Array.isArray(myCouponsResponse.data.coupons)) {
        const claimedCoupons = myCouponsResponse.data.coupons;
        const latestCoupon = claimedCoupons.find(c => 
          (c.coupon?.id || c.coupon_id || c.id) === coupon.id.toString() ||
          c.claim_id === coupon.claim_id ||
          (c.coupon && c.coupon.id === coupon.id.toString())
        );

        if (latestCoupon) {
          // console.log('Latest coupon data:', latestCoupon);
          // console.log('Latest coupon.coupon:', latestCoupon.coupon);
          // console.log('Latest coupon.coupon_code:', latestCoupon.coupon_code);
          // console.log('Latest coupon.couponCode:', latestCoupon.couponCode);
          // console.log('Latest coupon.kode_kupon:', latestCoupon.kode_kupon);
          setCoupon(prevCoupon => ({
            ...prevCoupon,
            // Update with latest data from API
            voucher_url: latestCoupon.voucher_url || prevCoupon.voucher_url,
            status: latestCoupon.status || prevCoupon.status,
            expiry_date: latestCoupon.expiry_date || prevCoupon.expiry_date,
            voucher_expiry_date: latestCoupon.voucher_expiry_date || prevCoupon.voucher_expiry_date,
            tipe_kupon: latestCoupon.tipe_kupon || prevCoupon.tipe_kupon,
            type: latestCoupon.tipe_kupon || prevCoupon.type || prevCoupon.tipe_kupon,
            is_claimed: true,
            claim_id: latestCoupon.claim_id || prevCoupon.claim_id,
            // For Indobest coupons, use voucher_code or generate from claim_id
            voucher_code: latestCoupon.voucher_code || latestCoupon.claim_id || prevCoupon.voucher_code,
            // Update coupon_code from the coupon object
            coupon_code: latestCoupon.coupon_code || (latestCoupon.coupon && latestCoupon.coupon.kode_kupon) || prevCoupon.coupon_code
          }));
        } else {
          console.log('Coupon not found in latest API response');
        }
      } else {
        // console.log('Unexpected response format:', myCouponsResponse);
        console.log('Unexpected response format');
      }
    } catch (error) {
      console.error('Error loading latest coupon data:', error);
      showCustomAlert('Error', 'Failed to load latest coupon data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (initialCoupon) {
      setLoading(false);
      loadLatestCouponData(); // Always refresh with latest data on mount
    } else {
      setLoading(false);
      showCustomAlert('Error', 'No coupon data provided');
      navigation.goBack();
    }
  }, []);

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#3B82F6" />
          <Text style={styles.loadingText}>Loading coupon details...</Text>
        </View>
      </SafeAreaView>
    );
  }

  const renderQRCode = () => {
    const voucherCode = coupon.coupon_code || coupon.couponCode;
    // console.log('QR Code coupon data:', coupon);
    if (!voucherCode || voucherCode === 'PENDING') {
      return (
        <View style={styles.qrPlaceholder}>
          <Ionicons name="time-outline" size={64} color="#94A3B8" />
          <Text style={styles.qrPlaceholderText}>
            {voucherCode === 'PENDING' ? 'Coupon code pending...' : 'No coupon code available'}
          </Text>
        </View>
      );
    }

    return (
      <View style={styles.qrContainer}>
        <View style={styles.qrCodeWrapper}>
          <QRCode
            value={voucherCode}
            size={Math.min(width * 0.5, 250)} // Responsive size, max 250px
            color="#000000"
            backgroundColor="#FFFFFF"
          />
        </View>
        <Text style={styles.qrCodeText}>{voucherCode}</Text>
      
      </View>
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      <AppHeader
        title="Kupon Indobest"
        onBack={() => navigation.goBack()}
        right={(
          <TouchableOpacity onPress={loadLatestCouponData} disabled={refreshing} hitSlop={8}>
            <Ionicons name="refresh" size={22} color={refreshing ? '#94A3B8' : '#1E293B'} />
          </TouchableOpacity>
        )}
      />

      <ScrollView style={styles.content}>
        {/* Coupon Image */}
        {coupon.image_url && (
          <Image
            source={{ uri: coupon.image_url }}
            style={styles.couponImage}
            resizeMode="contain"
          />
        )}

        {/* Coupon Details */}
        <View style={styles.detailsContainer}>
          <Text style={styles.couponTitle}>{coupon.title || coupon.nama}</Text>
          
          {coupon.description && (
            <Text style={styles.couponDescription}>{coupon.description}</Text>
          )}

          {/* QR Code Section */}
          <View style={styles.qrSection}>
            <Text style={styles.sectionTitle}>Kode Kupon</Text>
            {renderQRCode()}
          </View>

          {/* Coupon Details */}
          <View style={styles.detailsSection}>
            <Text style={styles.sectionTitle}>Detail Kupon</Text>
            
            {coupon.voucher_expiry_date && (
              <View style={styles.detailItem}>
                <Ionicons name="calendar-outline" size={20} color="#6B7280" />
                <Text style={styles.detailText}>
                  Berlaku hingga: {new Date(coupon.voucher_expiry_date).toLocaleDateString('id-ID')}
                </Text>
              </View>
            )}

            {coupon.coupon_code && (
              <View style={styles.detailItem}>
                <Ionicons name="barcode-outline" size={20} color="#6B7280" />
                <Text style={styles.detailText}>
                  Kode: {coupon.coupon_code}
                </Text>
              </View>
            )}

            {coupon.status && (
              <View style={styles.detailItem}>
                <Ionicons 
                  name={coupon.status === 'used' ? "checkmark-circle" : "information-circle"} 
                  size={20} 
                  color={coupon.status === 'used' ? "#10B981" : "#6B7280"} 
                />
                <Text style={styles.detailText}>
                  Status: {coupon.status === 'used' ? 'Telah Digunakan' : 'Tersedia'}
                </Text>
              </View>
            )}

            {coupon.terms_and_conditions && (
              <View style={styles.detailItem}>
                <Ionicons name="document-text-outline" size={20} color="#6B7280" />
                <Text style={styles.detailText}>Syarat & Ketentuan: {coupon.terms_and_conditions}</Text>
              </View>
            )}
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  content: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#6B7280',
  },
  couponImage: {
    width: '100%',
    height: 200,
  },
  detailsContainer: {
    padding: 20,
  },
  couponTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#1F2937',
    marginBottom: 8,
  },
  couponDescription: {
    fontSize: 16,
    color: '#6B7280',
    marginBottom: 24,
    lineHeight: 22,
  },
  qrSection: {
    marginBottom: 32,
    alignItems: 'center',
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 16,
  },
  qrContainer: {
    alignItems: 'center',
    padding: 20,
    backgroundColor: '#F9FAFB',
    borderRadius: 12,
    marginBottom: 16,
  },
  qrCodeWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 10,
  },
  qrPlaceholder: {
    alignItems: 'center',
    padding: 40,
    backgroundColor: '#F9FAFB',
    borderRadius: 12,
    marginBottom: 16,
  },
  qrPlaceholderText: {
    marginTop: 16,
    fontSize: 16,
    color: '#6B7280',
    textAlign: 'center',
  },
  qrCodeText: {
    marginTop: 16,
    fontSize: 18,
    fontWeight: '600',
    color: '#1F2937',
  },
  qrInstruction: {
    marginTop: 8,
    fontSize: 14,
    color: '#6B7280',
    textAlign: 'center',
  },
  detailsSection: {
    marginBottom: 32,
  },
  detailItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  detailText: {
    marginLeft: 12,
    fontSize: 16,
    color: '#6B7280',
    flex: 1,
  },
});